import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  TextField,
  Grid,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  Radio,
  PlayArrow,
  NetworkCheck,
  Album,
  CheckCircle,
  Error as ErrorIcon,
  HelpOutline,
} from '@mui/icons-material';
import { SectionTabs } from '../../components/SectionTabs';
import { SCHEDULING_TABS, SCHEDULING_ACCENT_COLOR } from './schedulingTabs';
import { schedulingHelp } from '../../components/PageHelp/helpContent';
import {
  useChannels,
  useCreateChannel,
  useUpdateChannel,
  useDeleteChannel,
  useTestChannel,
} from '../../hooks/useChannels';
import { useOrganizations, useSites } from '../../hooks/useFleet';
import { useOrgFilter } from '../../contexts/OrganizationContext';
import type {
  Channel,
  ChannelType,
  ChannelStreamType,
  ChannelRotationMode,
  ChannelTestResult,
} from '../../api/types';

type ChannelScope = 'platform' | 'organization' | 'site';

function deriveScope(channel: Channel): ChannelScope {
  if (channel.site_id) return 'site';
  if (channel.organization_id) return 'organization';
  return 'platform';
}

const SCOPE_LABELS: Record<ChannelScope, string> = {
  platform: 'Platform',
  organization: 'Organization',
  site: 'Site',
};

const SCOPE_COLORS: Record<ChannelScope, string> = {
  platform: '#0ea5e9',
  organization: '#8b5cf6',
  site: '#f59e0b',
};

const COLOR_OPTIONS = [
  '#ef4444', '#f97316', '#f59e0b', '#22c55e', '#10b981',
  '#06b6d4', '#3b82f6', '#6366f1', '#8b5cf6', '#ec4899',
];

const STREAM_TYPES: { value: ChannelStreamType; label: string }[] = [
  { value: 'HLS', label: 'HLS (.m3u8)' },
  { value: 'ICECAST', label: 'Icecast' },
  { value: 'SHOUTCAST', label: 'Shoutcast' },
  { value: 'RTMP', label: 'RTMP' },
  { value: 'HTTP', label: 'HTTP (MP3/AAC)' },
];

const ROTATION_MODES: { value: ChannelRotationMode; label: string }[] = [
  { value: 'sequential', label: 'Sequential (in order)' },
  { value: 'shuffle', label: 'Shuffle (random)' },
  { value: 'time_weighted', label: 'Time-weighted' },
];

interface ChannelFormData {
  scope: ChannelScope;
  organization_id: string;
  site_id: string;
  name: string;
  description: string;
  channel_type: ChannelType;
  stream_url: string;
  stream_type: ChannelStreamType;
  rotation_mode: ChannelRotationMode;
  crossfade_ms: number;
  color: string;
  is_active: boolean;
}

const initialFormData: ChannelFormData = {
  scope: 'organization',
  organization_id: '',
  site_id: '',
  name: '',
  description: '',
  channel_type: 'live_stream',
  stream_url: '',
  stream_type: 'HLS',
  rotation_mode: 'sequential',
  crossfade_ms: 8000,
  color: '#6366f1',
  is_active: true,
};

type TypeFilter = 'all' | ChannelType;

const StatusIndicator: React.FC<{ status?: string | null; checkedAt?: string | null }> = ({
  status,
  checkedAt,
}) => {
  if (!status) {
    return (
      <Tooltip title="Not yet checked">
        <HelpOutline sx={{ fontSize: 16, color: 'text.secondary' }} />
      </Tooltip>
    );
  }
  const ok = /^2\d\d$/.test(status);
  const label = checkedAt ? `${status} — ${new Date(checkedAt).toLocaleString()}` : status;
  return (
    <Tooltip title={label}>
      {ok ? (
        <CheckCircle sx={{ fontSize: 16, color: '#10b981' }} />
      ) : (
        <ErrorIcon sx={{ fontSize: 16, color: '#ef4444' }} />
      )}
    </Tooltip>
  );
};

const ChannelsPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingChannel, setEditingChannel] = useState<Channel | null>(null);
  const [formData, setFormData] = useState<ChannelFormData>(initialFormData);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Channel | null>(null);
  const [testResult, setTestResult] = useState<{ channel: Channel; result: ChannelTestResult } | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);

  const orgFilter = useOrgFilter();
  const { data: orgsData } = useOrganizations();
  const organizations = orgsData?.data || [];

  const { data, isLoading, error } = useChannels({
    organization_id: orgFilter,
    type: typeFilter === 'all' ? undefined : typeFilter,
    size: 100,
  });

  const createMutation = useCreateChannel();
  const updateMutation = useUpdateChannel();
  const deleteMutation = useDeleteChannel();
  const testMutation = useTestChannel();

  // Load sites for the currently selected org in the form (only needed when
  // scope === 'site'). Returns empty when no org is picked yet.
  const { data: sitesData } = useSites(
    formData.organization_id
      ? { organization_id: formData.organization_id, size: 200 }
      : undefined
  );
  const sites = formData.organization_id ? sitesData?.data || [] : [];

  const channels = data?.data || [];
  const filtered = searchQuery
    ? channels.filter((c) => c.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : channels;

  const handleOpenCreate = () => {
    setSubmitError(null);
    setEditingChannel(null);
    setFormData({
      ...initialFormData,
      scope: 'organization',
      organization_id: orgFilter || organizations[0]?.id || '',
      site_id: '',
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (channel: Channel) => {
    setSubmitError(null);
    setEditingChannel(channel);
    setFormData({
      scope: deriveScope(channel),
      organization_id: channel.organization_id ?? '',
      site_id: channel.site_id ?? '',
      name: channel.name,
      description: channel.description || '',
      channel_type: channel.channel_type,
      stream_url: channel.stream_url || '',
      stream_type: (channel.stream_type as ChannelStreamType) || 'HLS',
      rotation_mode: (channel.rotation_mode as ChannelRotationMode) || 'sequential',
      crossfade_ms: channel.crossfade_ms ?? 8000,
      color: channel.color || '#6366f1',
      is_active: Boolean(channel.is_active),
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingChannel(null);
    setFormData(initialFormData);
    setSubmitError(null);
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    try {
      const base = {
        name: formData.name,
        description: formData.description || undefined,
        color: formData.color,
        is_active: formData.is_active,
      };

      if (editingChannel) {
        // channel_type is immutable after creation
        const typeSpecific =
          editingChannel.channel_type === 'live_stream'
            ? { stream_url: formData.stream_url, stream_type: formData.stream_type }
            : { rotation_mode: formData.rotation_mode, crossfade_ms: formData.crossfade_ms };

        await updateMutation.mutateAsync({
          id: editingChannel.id,
          data: { ...base, ...typeSpecific },
        });
      } else {
        const typeSpecific =
          formData.channel_type === 'live_stream'
            ? { stream_url: formData.stream_url, stream_type: formData.stream_type }
            : { rotation_mode: formData.rotation_mode, crossfade_ms: formData.crossfade_ms };

        // Build scope-aware payload. Platform channels omit both IDs; site
        // channels send site_id (the API derives org from it).
        const scopeFields =
          formData.scope === 'platform'
            ? {}
            : formData.scope === 'organization'
            ? { organization_id: formData.organization_id }
            : { organization_id: formData.organization_id, site_id: formData.site_id };

        await createMutation.mutateAsync({
          ...base,
          ...scopeFields,
          channel_type: formData.channel_type,
          ...typeSpecific,
        });
      }
      handleCloseDialog();
    } catch (err: any) {
      setSubmitError(err?.response?.data?.error || err?.message || 'Failed to save channel');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      setDeleteTarget(null);
    } catch (err: any) {
      setSubmitError(err?.response?.data?.error || err?.message || 'Failed to delete channel');
    }
  };

  const handleTest = async (channel: Channel) => {
    setTestingId(channel.id);
    try {
      const result = await testMutation.mutateAsync(channel.id);
      setTestResult({ channel, result });
    } catch (err: any) {
      setSubmitError(err?.response?.data?.error || err?.message || 'Failed to test channel');
    } finally {
      setTestingId(null);
    }
  };

  const isSaveDisabled = (() => {
    if (!formData.name) return true;
    if (!editingChannel) {
      // Per-scope required fields: org requires org_id, site requires both.
      if (formData.scope === 'organization' && !formData.organization_id) return true;
      if (formData.scope === 'site' && (!formData.organization_id || !formData.site_id)) return true;
      // Platform channels are live-stream-only (backend enforces this too).
      if (formData.scope === 'platform' && formData.channel_type === 'mix_rotation') return true;
    }
    const type = editingChannel ? editingChannel.channel_type : formData.channel_type;
    if (type === 'live_stream' && !formData.stream_url) return true;
    if (type === 'mix_rotation' && (formData.crossfade_ms < 5000 || formData.crossfade_ms > 10000))
      return true;
    if (createMutation.isPending || updateMutation.isPending) return true;
    return false;
  })();

  return (
    <Box>
      <SectionTabs
        title="Scheduling"
        tabs={SCHEDULING_TABS}
        accentColor={SCHEDULING_ACCENT_COLOR}
        help={schedulingHelp.channels}
        actionButton={{
          label: 'New Channel',
          icon: <Add />,
          onClick: handleOpenCreate,
        }}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load channels. Please try again.
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          placeholder="Search channels..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
          size="small"
          sx={{ flex: 1, minWidth: 200 }}
        />
        <ToggleButtonGroup
          value={typeFilter}
          exclusive
          size="small"
          onChange={(_, v) => v && setTypeFilter(v)}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="live_stream">
            <Radio sx={{ fontSize: 18, mr: 0.5 }} /> Live
          </ToggleButton>
          <ToggleButton value="mix_rotation">
            <Album sx={{ fontSize: 18, mr: 0.5 }} /> Mix
          </ToggleButton>
        </ToggleButtonGroup>
      </Paper>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {filtered.map((channel) => {
            const isLive = channel.channel_type === 'live_stream';
            return (
              <Grid item xs={12} sm={6} md={4} key={channel.id}>
                <Card
                  sx={{
                    border: '2px solid',
                    borderColor: channel.color || '#6366f1',
                    transition: 'all 0.2s',
                    '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
                    opacity: channel.is_active ? 1 : 0.6,
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      {isLive ? (
                        <Radio sx={{ color: channel.color || '#6366f1' }} />
                      ) : (
                        <Album sx={{ color: channel.color || '#6366f1' }} />
                      )}
                      <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
                        {channel.name}
                      </Typography>
                      {isLive && (
                        <StatusIndicator
                          status={channel.last_check_status}
                          checkedAt={channel.last_checked_at}
                        />
                      )}
                      <IconButton size="small" onClick={() => handleOpenEdit(channel)}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => setDeleteTarget(channel)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>

                    {(() => {
                      const scope = deriveScope(channel);
                      const label =
                        scope === 'platform'
                          ? 'Platform'
                          : scope === 'site'
                          ? `${channel.organization_name ?? ''} · ${channel.site_name ?? ''}`
                          : channel.organization_name ?? '';
                      return label ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            size="small"
                            label={SCOPE_LABELS[scope]}
                            sx={{
                              bgcolor: `${SCOPE_COLORS[scope]}20`,
                              color: SCOPE_COLORS[scope],
                              fontWeight: 600,
                              height: 20,
                            }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {label}
                          </Typography>
                        </Box>
                      ) : null;
                    })()}

                    {channel.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                        {channel.description}
                      </Typography>
                    )}

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                      <Chip
                        size="small"
                        label={isLive ? 'Live Stream' : 'Mix Rotation'}
                        color={isLive ? 'primary' : 'secondary'}
                      />
                      {isLive && channel.stream_type && (
                        <Chip size="small" label={channel.stream_type} variant="outlined" />
                      )}
                      {!isLive && (
                        <Chip size="small" label={`${channel.mix_count ?? 0} mixes`} variant="outlined" />
                      )}
                      {!isLive && channel.rotation_mode && (
                        <Chip size="small" label={channel.rotation_mode} variant="outlined" />
                      )}
                      {(channel.zone_count ?? 0) > 0 && (
                        <Chip
                          size="small"
                          icon={<PlayArrow />}
                          label={`${channel.zone_count} zone${channel.zone_count === 1 ? '' : 's'}`}
                          sx={{ bgcolor: '#10b98120', color: '#10b981' }}
                        />
                      )}
                    </Box>

                    {isLive && (
                      <Button
                        size="small"
                        startIcon={
                          testingId === channel.id ? (
                            <CircularProgress size={14} />
                          ) : (
                            <NetworkCheck />
                          )
                        }
                        onClick={() => handleTest(channel)}
                        disabled={testingId === channel.id}
                        sx={{ mt: 2 }}
                      >
                        Test stream
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}

          {filtered.length === 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Radio sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No channels found
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Create a live stream channel or a DJ mix rotation.
                </Typography>
                <Button variant="contained" onClick={handleOpenCreate}>
                  Create Channel
                </Button>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingChannel ? 'Edit Channel' : 'Create New Channel'}</DialogTitle>
        <DialogContent>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
              {submitError}
            </Alert>
          )}
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {!editingChannel && (
              <Box>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Scope
                </Typography>
                <ToggleButtonGroup
                  value={formData.scope}
                  exclusive
                  size="small"
                  fullWidth
                  onChange={(_, v: ChannelScope | null) => {
                    if (!v) return;
                    setFormData({
                      ...formData,
                      scope: v,
                      // Platform channels force live_stream (server rejects mix).
                      channel_type: v === 'platform' ? 'live_stream' : formData.channel_type,
                      // Clear site when leaving site scope.
                      site_id: v === 'site' ? formData.site_id : '',
                    });
                  }}
                >
                  <ToggleButton value="platform">Platform</ToggleButton>
                  <ToggleButton value="organization">Organization</ToggleButton>
                  <ToggleButton value="site">Site</ToggleButton>
                </ToggleButtonGroup>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  {formData.scope === 'platform'
                    ? 'Available to every organization. Live streams only.'
                    : formData.scope === 'organization'
                    ? 'Private to one organization — all of its sites can tune in.'
                    : 'Private to a single site within an organization.'}
                </Typography>
              </Box>
            )}

            {!editingChannel && formData.scope !== 'platform' && (
              <FormControl fullWidth required>
                <InputLabel>Organization</InputLabel>
                <Select
                  value={formData.organization_id}
                  label="Organization"
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      organization_id: e.target.value as string,
                      site_id: '', // selecting a new org invalidates the site pick
                    })
                  }
                >
                  {organizations.map((org) => (
                    <MenuItem key={org.id} value={org.id}>
                      {org.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {!editingChannel && formData.scope === 'site' && (
              <FormControl fullWidth required disabled={!formData.organization_id}>
                <InputLabel>Site</InputLabel>
                <Select
                  value={formData.site_id}
                  label="Site"
                  onChange={(e) =>
                    setFormData({ ...formData, site_id: e.target.value as string })
                  }
                >
                  {sites.length === 0 ? (
                    <MenuItem value="" disabled>
                      {formData.organization_id
                        ? 'No sites in this organization'
                        : 'Select an organization first'}
                    </MenuItem>
                  ) : (
                    sites.map((site) => (
                      <MenuItem key={site.id} value={site.id}>
                        {site.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            )}

            {editingChannel && (
              <Alert severity="info">
                Scope: <strong>{SCOPE_LABELS[deriveScope(editingChannel)]}</strong>
                {' '}(cannot be changed after creation)
              </Alert>
            )}

            {!editingChannel && (
              <FormControl fullWidth>
                <InputLabel>Channel Type</InputLabel>
                <Select
                  value={formData.channel_type}
                  label="Channel Type"
                  onChange={(e) =>
                    setFormData({ ...formData, channel_type: e.target.value as ChannelType })
                  }
                >
                  <MenuItem value="live_stream">Live Stream (external URL)</MenuItem>
                  <MenuItem value="mix_rotation" disabled={formData.scope === 'platform'}>
                    Mix Rotation (DJ mix content)
                    {formData.scope === 'platform' ? ' — requires an organization' : ''}
                  </MenuItem>
                </Select>
              </FormControl>
            )}

            {editingChannel && (
              <Alert severity="info" sx={{ alignItems: 'center' }}>
                Type: <strong>{editingChannel.channel_type === 'live_stream' ? 'Live Stream' : 'Mix Rotation'}</strong>
                {' '}(cannot be changed after creation)
              </Alert>
            )}

            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />

            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>Color</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {COLOR_OPTIONS.map((color) => (
                  <Box
                    key={color}
                    onClick={() => setFormData({ ...formData, color })}
                    sx={{
                      width: 32, height: 32, borderRadius: 1, bgcolor: color, cursor: 'pointer',
                      border: formData.color === color ? '3px solid white' : 'none',
                      boxShadow: formData.color === color ? `0 0 0 2px ${color}` : 'none',
                    }}
                  />
                ))}
              </Box>
            </Box>

            {/* Live Stream fields */}
            {(editingChannel?.channel_type || formData.channel_type) === 'live_stream' && (
              <>
                <TextField
                  label="Stream URL"
                  value={formData.stream_url}
                  onChange={(e) => setFormData({ ...formData, stream_url: e.target.value })}
                  fullWidth
                  required
                  placeholder="https://stream.example.com/live.m3u8"
                />
                <FormControl fullWidth required>
                  <InputLabel>Stream Type</InputLabel>
                  <Select
                    value={formData.stream_type}
                    label="Stream Type"
                    onChange={(e) =>
                      setFormData({ ...formData, stream_type: e.target.value as ChannelStreamType })
                    }
                  >
                    {STREAM_TYPES.map((t) => (
                      <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}

            {/* Mix Rotation fields */}
            {(editingChannel?.channel_type || formData.channel_type) === 'mix_rotation' && (
              <>
                <FormControl fullWidth>
                  <InputLabel>Rotation Mode</InputLabel>
                  <Select
                    value={formData.rotation_mode}
                    label="Rotation Mode"
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        rotation_mode: e.target.value as ChannelRotationMode,
                      })
                    }
                  >
                    {ROTATION_MODES.map((m) => (
                      <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Crossfade (ms)"
                  type="number"
                  value={formData.crossfade_ms}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      crossfade_ms: parseInt(e.target.value) || 8000,
                    })
                  }
                  fullWidth
                  helperText="Between 5000 and 10000 ms"
                  inputProps={{ min: 5000, max: 10000, step: 500 }}
                />
                {!editingChannel && (
                  <Alert severity="info">
                    After creating this channel, add DJ mixes to the rotation from the channel's detail page.
                  </Alert>
                )}
              </>
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={isSaveDisabled}>
            {createMutation.isPending || updateMutation.isPending ? (
              <CircularProgress size={20} />
            ) : editingChannel ? (
              'Save Changes'
            ) : (
              'Create Channel'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Channel?</DialogTitle>
        <DialogContent>
          <Typography>
            Delete <strong>{deleteTarget?.name}</strong>?
            {deleteTarget?.channel_type === 'mix_rotation' && (deleteTarget.mix_count ?? 0) > 0 && (
              <> All {deleteTarget.mix_count} rotation assignments will be removed.</>
            )}
            {(deleteTarget?.zone_count ?? 0) > 0 && (
              <>
                {' '}This channel is selected by {deleteTarget?.zone_count} zone(s) and cannot be
                deleted until reassigned.
              </>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending || (deleteTarget?.zone_count ?? 0) > 0}
          >
            {deleteMutation.isPending ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Test Result */}
      <Dialog open={!!testResult} onClose={() => setTestResult(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Stream Test: {testResult?.channel.name}</DialogTitle>
        <DialogContent>
          {testResult && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
              <Alert severity={testResult.result.reachable ? 'success' : 'error'}>
                {testResult.result.reachable
                  ? `Reachable (HTTP ${testResult.result.http_status}) in ${testResult.result.response_time_ms}ms`
                  : `Failed: ${testResult.result.error || 'unreachable'}`}
              </Alert>
              <Typography variant="body2">
                <strong>URL:</strong> {testResult.result.stream_url}
              </Typography>
              <Typography variant="body2">
                <strong>Content-Type:</strong> {testResult.result.content_type || '—'}
              </Typography>
              <Typography variant="body2">
                <strong>Configured type:</strong> {testResult.result.configured_type || '—'}
              </Typography>
              <Typography variant="body2">
                <strong>Detected type:</strong> {testResult.result.detected_type || '—'}
                {testResult.result.type_matches === false && (
                  <Chip size="small" label="Mismatch" color="warning" sx={{ ml: 1 }} />
                )}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestResult(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChannelsPage;
